/**
 * Promotional Items — the cart page's panel and watcher (the DOM half).
 *
 * The rules are pure in `services/promotionRules.ts`; this file reads the two
 * Liquid data islands, calls `/cart.js`, draws the panel and runs the watcher.
 * Design: `C:\Repos\scanner-dev\Promotional Items.md` §6.2–6.3 and §16.5.
 *
 * ⚠ The cart page is server-rendered (totals, warehouse groups, the shipping
 *   quote), so every change here ends in a RELOAD, exactly as Clear Cart and
 *   Restore do — and it reaches cart sync BEFORE the reload (`pushDiffNow`).
 *   A change left for "drift" can be undone by the next pull; an emptied cart is
 *   even read as state loss and restored. So a removal that would EMPTY the cart
 *   goes through `requestClear` (server first, with clear evidence).
 * ⚠ The watcher runs whenever the cart holds a free item, WHATEVER the
 *   metafield says: at load, after every cart-sync apply (`cart-sync:applied`),
 *   after the page's own trash button, and before checkout. A reload guard stops
 *   it looping if Shopify refuses a change.
 * ⚠ Preview rows (Scanner's `PROMOTIONS_PREVIEW`) show the panel only to a tester
 *   who adds `?promo_preview=1`; a customer never sees a preview.
 * ⚠ The box shows ONLY when the promotion can apply and has not been applied
 *   (`shouldShowPromotionPanel`, owner 2026-09-27): the cart earns a row in effect
 *   and holds no free item of its key. There is no change-size control — the
 *   customer deletes the free line and adds it again. The page re-asks after the
 *   changes it makes WITHOUT a reload — its trash button and a cart-sync apply both
 *   call `promotionCartChanged`.
 * ⚠ Nothing here writes HTML from data — every string goes in through
 *   `textContent`, so a label or colour name can never inject markup.
 */

import type { CartDiffResult, CartItem } from "../types/cart.js";
import { executeCartOperations, fetchCart } from "../services/cartService.js";
import { pushDiffNow, requestClear } from "../services/cartSync/syncService.js";
import {
  describeWatcherReason,
  isEmptyPlan,
  isRowInEffect,
  normalizePromotionCode,
  parsePromotionMetafield,
  parsePromotionProducts,
  planAddFreeItem,
  planWatcher,
  qualifies,
  rowForCode,
  shouldShowPromotionPanel,
  sizeChoices,
  type PromotionCartPlan,
  type PromotionEntry,
  type PromotionMetafield,
  type PromotionProductInfo,
} from "../services/promotionRules.js";

const ROOT_SELECTOR = "[data-promo-root]";
const METAFIELD_ISLAND_ID = "wp-promotions-data";
const PRODUCTS_ISLAND_ID = "wp-promotion-products";
const PREVIEW_PARAM = "promo_preview";
/** sessionStorage: a sentence to show once the page has reloaded. */
const NOTICE_KEY = "wp-promo-notice";
/** sessionStorage: reloads the watcher caused recently — the loop guard. */
const RELOAD_GUARD_KEY = "wp-promo-watcher-reloads";
const RELOAD_GUARD_WINDOW_MS = 60_000;
const RELOAD_GUARD_MAX = 2;
const NOT_QUALIFIED = "Your cart does not qualify yet:";

let loaded = false;
let metafield: PromotionMetafield | null = null;
let products: PromotionProductInfo[] = [];
let watcherRunning = false;
/** A trigger arrived while the watcher ran — run once more when it finishes. */
let rerunRequested = false;
/** A change is on its way to a reload — nothing else may start one. */
let reloading = false;
/** An add is in flight — Apply must not re-enable the button under it. */
let adding = false;
/** Re-judge whether the built panel shows; set once the panel is built. */
let refreshPanel: (() => Promise<void>) | null = null;
/** A cart change arrived before the panel was built, or during an add — judge it once it can be. */
let refreshPending = false;

function log(message: string): void {
  console.debug(`[promotions] ${message}`);
}

function readIsland(id: string): unknown {
  const el = document.getElementById(id);
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent) as unknown;
  } catch (error: unknown) {
    log(`data island ${id} is not JSON: ${error instanceof Error ? error.message : String(error)}`);
    return null;
  }
}

/** Read the islands once (the watcher may be triggered before the panel boots). */
function ensureLoaded(): void {
  if (loaded) return;
  loaded = true;
  metafield = parsePromotionMetafield(readIsland(METAFIELD_ISLAND_ID));
  products = parsePromotionProducts(readIsland(PRODUCTS_ISLAND_ID));
}

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function toDiff(plan: PromotionCartPlan): CartDiffResult {
  return {
    toDelete: plan.remove.map((r) => ({
      key: `${r.variantId}|${r.warehouseCode}`,
      lineKey: r.lineKey,
      variantId: String(r.variantId),
      warehouseCode: r.warehouseCode,
      quantity: r.quantity,
    })),
    toUpdate: plan.setQuantity.map((u) => ({
      key: `${u.variantId}|${u.warehouseCode}`,
      lineKey: u.lineKey,
      variantId: String(u.variantId),
      warehouseCode: u.warehouseCode,
      oldQty: u.from,
      newQty: u.to,
    })),
    toAdd: plan.add.map((a) => ({
      key: `${a.variantId}|${a.warehouseCode}`,
      variantId: String(a.variantId),
      warehouseCode: a.warehouseCode,
      quantity: a.quantity,
    })),
  };
}

async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Show a sentence where the cart page shows its status messages, else in the panel, else as a popup. */
function showNotice(text: string, root: HTMLElement | null): void {
  const status = document.querySelector<HTMLElement>(".cart-status-message");
  if (status) {
    status.textContent = text;
    status.classList.add("visible");
    return;
  }
  const own = root?.querySelector<HTMLElement>(".promo-panel__message");
  if (own) {
    own.textContent = text;
    return;
  }
  // An emptied cart has neither — the reload still owes the customer the reason.
  if (typeof window.showCustomPopup === "function") window.showCustomPopup(text);
}

/** Read AND clear the pending notice, so it can never surface on a later, unrelated visit. */
function takePendingNotice(): string | null {
  const store = safeSession();
  const text = store?.getItem(NOTICE_KEY) ?? null;
  if (text) store?.removeItem(NOTICE_KEY);
  return text;
}

function reloadWithNotice(text: string): void {
  reloading = true;
  safeSession()?.setItem(NOTICE_KEY, text);
  window.location.reload();
}

/** True when the watcher may reload again (no more than twice a minute). */
function reloadAllowed(): boolean {
  const store = safeSession();
  const now = Date.now();
  let recent: number[] = [];
  try {
    recent = (JSON.parse(store?.getItem(RELOAD_GUARD_KEY) ?? "[]") as unknown[])
      .filter((t): t is number => typeof t === "number" && now - t < RELOAD_GUARD_WINDOW_MS);
  } catch {
    recent = [];
  }
  if (recent.length >= RELOAD_GUARD_MAX) return false;
  store?.setItem(RELOAD_GUARD_KEY, JSON.stringify([...recent, now]));
  return true;
}

/** Would this plan leave the cart with no lines at all? */
function emptiesCart(items: readonly CartItem[], plan: PromotionCartPlan): boolean {
  const removed = new Set(plan.remove.map((r) => r.lineKey));
  return plan.add.length === 0 && items.every((i) => removed.has(i.key));
}

/**
 * The watcher. Returns true when it changed the cart (and a reload is on its way).
 * Exposed on `window` for the cart page's own trash button and checkout handoff.
 */
export async function runPromotionWatcher(): Promise<boolean> {
  if (!document.querySelector(ROOT_SELECTOR) || reloading) return reloading;
  if (watcherRunning) {
    rerunRequested = true;
    return false;
  }
  ensureLoaded();
  watcherRunning = true;
  let changed = false;
  try {
    const cart = await fetchCart();
    const plan = planWatcher(cart.items, metafield, Date.now());
    if (isEmptyPlan(plan)) return false;
    if (!reloadAllowed()) {
      log("the watcher changed the cart twice in a minute and it still is not right — stopping here");
      return false;
    }
    const diff = toDiff(plan);
    if (emptiesCart(cart.items, plan)) {
      // Server first, with clear evidence, or the next pull reads the empty cart
      // as lost data and puts the free item straight back.
      const cleared = await requestClear();
      if (!cleared.ok) {
        log(`could not clear through cart sync: ${cleared.reason}`);
        return false;
      }
      await executeCartOperations(diff);
    } else {
      await executeCartOperations(diff);
      if (!(await pushDiffNow(diff))) log("cart sync did not take the change now; it will catch up through drift");
    }
    const why = [...new Set(plan.reasons.map((r) => describeWatcherReason(r.reason)))].join("; ");
    changed = true;
    reloadWithNotice(`Your free promotion item was ${plan.remove.length > 0 ? "removed" : "adjusted"}: ${why}.`);
    return true;
  } catch (error: unknown) {
    log(`watcher failed: ${error instanceof Error ? error.message : String(error)}`);
    return false;
  } finally {
    watcherRunning = false;
    if (rerunRequested && !changed) {
      rerunRequested = false;
      void runPromotionWatcher();
    }
  }
}

// ── The panel ────────────────────────────────────────────────────────────────

interface PanelElements {
  readonly root: HTMLElement;
  readonly code: HTMLInputElement;
  readonly apply: HTMLButtonElement;
  readonly message: HTMLElement;
  readonly offer: HTMLElement;
  readonly offerLabel: HTMLElement;
  readonly size: HTMLSelectElement;
  readonly add: HTMLButtonElement;
  readonly missing: HTMLElement;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function buildPanel(root: HTMLElement): PanelElements {
  root.replaceChildren();
  const box = el("div", "promo-panel__box");
  box.append(el("h3", "promo-panel__title", "Promotion code"));

  const row = el("div", "promo-panel__row");
  const code = el("input", "promo-panel__code");
  code.type = "text";
  code.maxLength = 30;
  code.autocomplete = "off";
  code.setAttribute("aria-label", "Promotion code");
  code.placeholder = "Enter a promotion code";
  const apply = el("button", "btn promo-panel__apply", "Apply");
  apply.type = "button";
  row.append(code, apply);

  const message = el("div", "promo-panel__message");
  message.setAttribute("role", "status");
  message.setAttribute("aria-live", "polite");

  const offer = el("div", "promo-panel__offer");
  offer.hidden = true;
  const offerLabel = el("div", "promo-panel__label");
  const sizeLabel = el("label", "promo-panel__size-label", "Size ");
  const size = el("select", "promo-panel__size");
  sizeLabel.append(size);
  const add = el("button", "btn promo-panel__add", "Add free item");
  add.type = "button";
  const missing = el("ul", "promo-panel__missing");
  offer.append(offerLabel, sizeLabel, add, missing);

  box.append(row, message, offer);
  root.append(box);
  return { root, code, apply, message, offer, offerLabel, size, add, missing };
}

function productFor(row: PromotionEntry): PromotionProductInfo | null {
  return products.find((p) => p.handle === row.placeholderProductHandle) ?? null;
}

/** Fill the size list; keep `preferred` when it can be chosen, else the row's default, else the first choosable. */
function fillSizes(els: PanelElements, row: PromotionEntry, preferred: string | null): boolean {
  const choices = sizeChoices(row, productFor(row));
  els.size.replaceChildren();
  for (const c of choices) {
    const option = el("option", "", c.selectable ? c.label : `${c.label} (sold out)`);
    option.value = c.variantId === null ? "" : String(c.variantId);
    option.disabled = !c.selectable;
    option.dataset.size = c.size;
    els.size.append(option);
  }
  const pick = choices.find((c) => c.selectable && c.size === preferred)
    ?? choices.find((c) => c.selectable && c.size === row.defaultSize.toUpperCase())
    ?? choices.find((c) => c.selectable);
  if (pick) els.size.value = String(pick.variantId);
  return pick !== undefined;
}

async function showOffer(els: PanelElements, row: PromotionEntry): Promise<void> {
  els.offer.hidden = false;
  els.offerLabel.textContent = `${row.label} — ${row.colorName}`;
  // Keep the size already chosen when the offer is judged again (a cart change).
  const chosen = els.size.selectedOptions[0]?.dataset.size ?? null;
  const anySize = fillSizes(els, row, chosen);
  els.missing.replaceChildren();
  if (productFor(row) === null) {
    els.message.textContent = "This free item is not available on your account.";
    els.add.disabled = true;
    return;
  }
  if (!anySize) {
    els.message.textContent = "Every size of this free item is sold out.";
    els.add.disabled = true;
    return;
  }
  const cart = await fetchCart();
  const q = qualifies(cart.items, row);
  for (const m of q.missing) els.missing.append(el("li", "", m));
  els.add.disabled = adding || !q.qualifies;
  if (!q.qualifies) els.message.textContent = NOT_QUALIFIED;
  // Judged again after a cart change: a cart that now qualifies drops the old sentence.
  else if (els.message.textContent === NOT_QUALIFIED) els.message.textContent = "";
}

/** The part of a plan that ADDS, and the part that removes or trims — run in that order. */
function splitPlan(plan: PromotionCartPlan): { addFirst: PromotionCartPlan; thenRest: PromotionCartPlan } {
  return {
    addFirst: { remove: [], setQuantity: [], add: plan.add },
    thenRest: { remove: plan.remove, setQuantity: plan.setQuantity, add: [] },
  };
}

async function addFreeItem(els: PanelElements, row: PromotionEntry): Promise<void> {
  const variantId = Number(els.size.value);
  if (adding || !Number.isInteger(variantId) || variantId <= 0) return;
  // The page may have stayed open across the row's end (the week's colour change).
  if (!isRowInEffect(row, Date.now())) {
    els.message.textContent = "This promotion has ended.";
    els.add.disabled = true;
    return;
  }
  adding = true;
  els.add.disabled = true;
  els.message.textContent = "Adding…";
  try {
    const cart = await fetchCart();
    // Judged again on the cart as it is NOW — another tab or device may have changed it.
    const q = qualifies(cart.items, row);
    if (!q.qualifies) {
      els.message.textContent = NOT_QUALIFIED;
      els.missing.replaceChildren(...q.missing.map((m) => el("li", "", m)));
      return;
    }
    const plan = planAddFreeItem(cart.items, row, variantId);
    if (isEmptyPlan(plan)) {
      els.message.textContent = "Your free item is already in your cart.";
      return;
    }
    // ADD FIRST, then remove the old size or colour: if the add fails, the
    // customer still has the free item they had. If the removal fails after it,
    // the watcher's cap keeps the colour in effect, newest first — the new line.
    const { addFirst, thenRest } = splitPlan(plan);
    if (!isEmptyPlan(addFirst)) await executeCartOperations(toDiff(addFirst));
    if (!isEmptyPlan(thenRest)) await executeCartOperations(toDiff(thenRest));
    if (!(await pushDiffNow(toDiff(plan)))) log("cart sync did not take the add now; it will catch up through drift");
    const sizeText = els.size.selectedOptions[0]?.textContent ?? "";
    reloadWithNotice(`Your free ${row.colorName} ${row.label.replace(/^free\s+/i, "")} (${sizeText}) is in your cart.`);
  } catch (error: unknown) {
    log(`add failed: ${error instanceof Error ? error.message : String(error)}`);
    els.message.textContent = "We couldn't add the free item. Please refresh the page and try again.";
  } finally {
    if (!reloading) {
      adding = false;
      els.add.disabled = false;
      // A cart change arrived while the add ran and was held back: judge it now.
      if (refreshPending) {
        refreshPending = false;
        void refreshPanel?.();
      }
    }
  }
}

async function renderPanel(root: HTMLElement): Promise<void> {
  const now = Date.now();
  if (metafield === null) return;
  const preview = new URLSearchParams(window.location.search).get(PREVIEW_PARAM) === "1";
  if (metafield.preview && !preview) return;
  if (!metafield.promotions.some((r) => isRowInEffect(r, now))) return;

  // Built whenever a row is in effect, but SHOWN only when it can apply (the root
  // starts hidden in the Liquid, so a cart that does not qualify never flashes it).
  const els = buildPanel(root);
  const previewNote = metafield.preview ? "Preview — this panel is visible to testers only." : "";
  els.message.textContent = previewNote;

  let active: PromotionEntry | null = null;
  /** Orders overlapping refreshes: only the newest one may draw. */
  let refreshSeq = 0;

  /**
   * Show or hide the panel for the cart `items` (`shouldShowPromotionPanel`: the cart
   * earns a promotion and does not hold its free item yet). There is no change-size
   * control (owner, 2026-09-27): a customer deletes the free line and adds it again,
   * and this brings the box back the moment the line goes.
   */
  async function judge(items: readonly CartItem[], t: number): Promise<void> {
    const show = shouldShowPromotionPanel(items, metafield, products, t, preview);
    // The page may have stayed open across the row's end: never offer an ended row.
    if (active !== null && !isRowInEffect(active, t)) {
      active = null;
      els.offer.hidden = true;
    }
    if (!show) {
      // Nothing to offer: start clean if the cart earns it again.
      root.hidden = true;
      active = null;
      els.offer.hidden = true;
      els.missing.replaceChildren();
      els.message.textContent = previewNote;
      return;
    }
    // An offer still open: judge it again against the cart as it is now.
    if (active !== null && !els.offer.hidden) await showOffer(els, active);
    // Revealed only once drawn, so the offer never pops in after the box.
    root.hidden = false;
  }

  async function onApply(): Promise<void> {
    if (metafield === null || adding) return;
    const typed = normalizePromotionCode(els.code.value);
    if (typed.length === 0) return;
    els.message.textContent = "";
    const row = rowForCode(metafield, await sha256Hex(typed), Date.now());
    if (row === null) {
      els.offer.hidden = true;
      active = null;
      els.message.textContent = "That code isn't valid right now. Promotion codes are entered here, not at checkout.";
      return;
    }
    active = row;
    await showOffer(els, row);
  }

  els.apply.addEventListener("click", () => { void onApply(); });
  els.code.addEventListener("keydown", (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      // The code box sits inside the cart form; Enter must not submit to checkout.
      event.preventDefault();
      void onApply();
    }
  });
  els.add.addEventListener("click", () => {
    if (active) void addFreeItem(els, active);
  });

  // Listeners first, then the first draw: the box is revealed only by `judge`.
  const cart = await fetchCart();
  await judge(cart.items, now);

  refreshPanel = async (): Promise<void> => {
    if (reloading || metafield === null) return;
    if (adding) {
      // An add owns the panel and ends in a reload; if it does not, it runs this then.
      refreshPending = true;
      return;
    }
    const seq = ++refreshSeq;
    const latest = await fetchCart();
    // A newer refresh (two quick trashes), an add or a reload took over meanwhile.
    if (seq !== refreshSeq || reloading || adding) return;
    await judge(latest.items, Date.now());
  };
  // A cart change that landed while the panel was being built (cart sync boots first).
  if (refreshPending) {
    refreshPending = false;
    await refreshPanel();
  }
}

/**
 * The page changed the cart WITHOUT a reload (its trash button, a cart-sync apply):
 * the watcher first — it may remove a free item the cart no longer earns, which
 * reloads — then whether the box still shows. Exposed on `window` for the trash button.
 */
export async function promotionCartChanged(): Promise<void> {
  try {
    if (await runPromotionWatcher()) return;
    if (refreshPanel) await refreshPanel();
    else refreshPending = true;
  } catch (error: unknown) {
    log(`re-judging the panel failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/** Boot on the cart page (a no-op everywhere else, bar showing a notice a reload carried). */
export async function initPromotions(): Promise<void> {
  const notice = takePendingNotice();
  const root = document.querySelector<HTMLElement>(ROOT_SELECTOR);
  if (notice) showNotice(notice, root);
  if (!root) return;
  ensureLoaded();
  try {
    if (await runPromotionWatcher()) return;
    await renderPanel(root);
  } catch (error: unknown) {
    log(`panel failed: ${error instanceof Error ? error.message : String(error)}`);
    root.hidden = true;
  }
}

// Every cart-sync apply is judged, including one that lands before the panel boots
// or before the grid's hook is registered (the watcher is a no-op off the cart page).
if (typeof document !== "undefined") {
  document.addEventListener("cart-sync:applied", () => {
    void promotionCartChanged();
  });
}
